import { Button, Stack } from "@mui/material";
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useForm } from "react-hook-form";
import Divider from "components/Divider/Divider";
import { Controller } from "react-hook-form";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps } from "../utils";
import Datatable from "components/Datatable/Datatable";
import { useTranslation } from "react-i18next";
import merchantManagement from "routes/merchantManagement";
import { checkNonMoneyDataForTable, formPreventEnterSubmit } from "utils/utils";
import { queryEmployeeList, changePassword } from "../model";
import { spinning, filteredFalsyData } from "../utils";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import sha256 from "crypto-js/sha256";
import ChangePasswordDialog from "pages/merchant-management/components/change-password/ChangePasswordDialog";
import useActionSnackBar from "utils/useActionSnackBar/useActionSnackBar";
import useColors from "utils/useColors/useColors";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import responseCodeMapping from "utils/response_code_mapping.json";
const employeeListCache = {};

const MerchantEmployee = ({
  merchantId,
  merchantName,
  merchantPayfacPrefix,
  merchantStatus,
  redirectFn,
}) => {
  const c = useColors();
  const formProps = useForm({
    mode: "onChange",
  });
  const { control, handleSubmit, reset } = formProps;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const DIALOG_LOCALE_KEY = "page.dialogs.";
  const PAGE_LOCALE_KEY = "page.merchants";
  const { t } = useTranslation();
  const [datasource, setDatasource] = useState({
    data: [],
    total: 0,
    page: 0,
    size: 20,
  });
  const { snackBar } = useActionSnackBar();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);
  const isMerchantTerminated =
    String(merchantStatus || "").toUpperCase() === "TERMINATED";
  const CHANGEPASSWORD_INPUT_INIT_STATE = {
    id: "",
    newPassword: "",
  };
  const updateChangePasswordPayload = (fieldInput) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      newPassword: fieldInput.newPassword,
    });
  };
  const restoreChangePasswordDialog = () => {
    setChangePasswordDialogData(CHANGEPASSWORD_INPUT_INIT_STATE);
  };
  const [changePasswordDialogData, setChangePasswordDialogData] = useState(
    CHANGEPASSWORD_INPUT_INIT_STATE,
  );

  const openChangePasswordDialog = (data) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      id: data.employeeId,
      email: data.email,
    });
    setIsChangePasswordDialogOpen(true);
  };

  const closeChangePasswordDialog = () => {
    restoreChangePasswordDialog();
    setIsChangePasswordDialogOpen(false);
  };

  const ChangePasswordDialogContent = () => {
    return (
      <ChangePasswordDialog
        userId={changePasswordDialogData.email}
        updatePayload={updateChangePasswordPayload}
      />
    );
  };

  const updateChangePasswordResultCallback = async () => {
    if (changePasswordDialogData.newPassword == "") {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        employeeId: changePasswordDialogData.id,
        adminEmail: changePasswordDialogData.email,
        email: changePasswordDialogData.email,
        newPassword: sha256(changePasswordDialogData.newPassword).toString(),
      };

      const apiResponse = await changePassword(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackBar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout();
          }
          case 403: {
            snackBar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackBar(
                t(DIALOG_LOCALE_KEY + "changePassword.msgFailed") +
                  (responseCodeMapping[responseBody.responseCode]?.["en"] ||
                    responseCodeMapping.error?.["en"] ||
                    ""),
                "error",
              );
            } else {
              snackBar(
                t(DIALOG_LOCALE_KEY + "changePassword.msgSuccess"),
                "success",
              );
            }
          }
        }
      }
      closeChangePasswordDialog();
      handleSearch();
    } finally {
      setSubmitting(false);
    }
  };

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const syncDatasourceState = (responseBody) => {
    if (!responseBody) {
      return;
    }
    const nextPage = responseBody.page ?? 0;
    const nextSize = responseBody.size ?? 20;
    setDatasource(responseBody);
    setCurrentPage(nextPage);
    setPageSize(nextSize);
    pageSizeInfoRef.current.currentpage = nextPage;
    pageSizeInfoRef.current.pagesize = nextSize;
  };

  const handleSearch = useCallback(
    async (data) => {
      const sanitizedData = filteredFalsyData(data || {});
      setLoading(true);
      const payload = {
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
        action: "merchant-detail",
        sort: "employeeId,desc",
        merchantId,
        ...sanitizedData,
      };
      const apiResponse = await queryEmployeeList(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackBar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout();
          }
          case 403: {
            snackBar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode === "0000") {
              if (responseBody.data.length === 0) {
                snackBar(t("general.message.no.result"), "warning");
              }
              syncDatasourceState(responseBody);
              if (Object.keys(sanitizedData).length === 0) {
                employeeListCache[merchantId] = {
                  data: responseBody,
                };
              }
            } else {
              snackBar(
                t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
                "error",
              );
              setDatasource({
                total: 0,
                page: 0,
                size: 20,
                data: [],
              });
            }
          }
        }
      }
      setLoading(false);
    },
    [merchantId, pageSizeInfoRef, snackBar, t],
  );

  useEffect(() => {
    if (!merchantId) {
      return;
    }
    const cachedEntry = employeeListCache[merchantId];
    const cachedData = cachedEntry?.data;
    if (cachedData) {
      syncDatasourceState(cachedData);
      const hasCachedEmployees =
        Array.isArray(cachedData?.data) && cachedData.data.length > 0;
      if (hasCachedEmployees) {
        return;
      }
    }
    handleSearch();
  }, [merchantId]);

  const setAllValues = () => {
    reset({
      employeeId: "",
      email: "",
    });
  };

  const handleCreateEmployee = () => {
    const path = "/merchant-management/employees/add-offline-employee";
    const redirectpayload = {
      name: merchantManagement
        .find((tab) => tab.tabName === "Employees")
        .page.find((page) => page.path === path).name,
      path: path,
      refresh: true,
      state: {
        merchantId: merchantId,
        merchantName,
        payfacPrefix: merchantPayfacPrefix,
      },
    };
    redirectFn("redirectPage", redirectpayload);
  };

  const searchFields = () => (
    <form noValidate onSubmit={handleSearch} onKeyDown={formPreventEnterSubmit}>
      <Stack direction={"column"} rowGap={2} width={"80%"}>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
          <Controller
            name="employeeId"
            control={control}
            render={({ field: { value, onChange }, fieldState: {} }) => (
              <OutlinedText
                errorMessage={"page.finance.refund.list.id.error.message"}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}.merchantDetail.employee.searchFields.employeeId`,
                  ),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            // rules={{ required: "Gender is required" }}
            render={({ field: { value, onChange } }) => (
              <OutlinedText
                errorMessage={"page.finance.refund.list.id.error.message"}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}.merchantDetail.employee.searchFields.email`,
                  ),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack
        direction={"row"}
        gap={1}
        flexWrap={"wrap"}
        justifyContent={"flex-end"}
      >
        <ButtonMAS
          variant="secondary"
          onClick={async () => {
            setAllValues();
            await handleSearch();
          }}
          disabled={loading}
        >
          {t(`${PAGE_LOCALE_KEY}.merchantDetail.button.reset`)}
        </ButtonMAS>
        <ButtonMAS
          onClick={handleSubmit(handleSearch)}
          type="submit"
          isLoading={loading}
          disabled={loading}
        >
          {t(`${PAGE_LOCALE_KEY}.merchantDetail.button.search`)}
        </ButtonMAS>
      </Stack>
    </form>
  );

  const columns = [
    {
      key: "employeeId",
      title: t(
        PAGE_LOCALE_KEY + ".merchantDetail.employee.columnTitle.employeeId",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "email",
      title: t(PAGE_LOCALE_KEY + ".merchantDetail.employee.columnTitle.email"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "terminalSerialNumber",
      title: t(
        PAGE_LOCALE_KEY +
          ".merchantDetail.employee.columnTitle.terminalSerialNumber",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + ".merchantDetail.employee.columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + ".merchantDetail.employee.columnTitle.action"),
      sorter: false,
      render: (data) => {
        return (
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Button
              disabled={loading}
              sx={{ color: c.brandDark12 }}
              onClick={() => {
                const path =
                  "/merchant-management/employees/offline-employee-detail";
                const payload = {
                  name:
                    merchantManagement
                      .find((tab) => tab.tabName === "Employees")
                      .page.find((page) => page.path === path).name +
                    data.employeeId,
                  path: path,
                  state: {
                    employeeId: data.employeeId,
                  },
                };
                console.log("payload", payload);
                redirectFn("redirectPage", payload);
              }}
            >
              {t(`${PAGE_LOCALE_KEY}.merchantDetail.button.edit`)}
            </Button>
            <Button
              disabled={loading}
              sx={{ color: c.brandDark12 }}
              onClick={() => {
                openChangePasswordDialog(data);
              }}
            >
              {t(`${PAGE_LOCALE_KEY}.merchantDetail.button.changePassword`)}
            </Button>
          </Stack>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      {searchFields()}
      <Divider style={{ margin: "24px 0" }} />
      <Stack direction={"row"} justifyContent={"flex-end"} sx={{ mb: 2 }}>
        <ButtonMAS
          disabled={loading || isMerchantTerminated}
          onClick={handleCreateEmployee}
        >
          {t("common.create")}
        </ButtonMAS>
      </Stack>
      <Stack>
        {loading ? (
          spinning()
        ) : (
          <Datatable
            columns={columns}
            datasource={datasource?.data}
            total={datasource?.total}
            pagination={pagination}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSizeInfoRef={pageSizeInfoRef}
            handleSearch={handleSearch}
          />
        )}
      </Stack>
      <SimpleDialog
        open={isChangePasswordDialogOpen}
        dialogOncloseFn={closeChangePasswordDialog}
        title={t(DIALOG_LOCALE_KEY + "changePassword.title")}
        content={ChangePasswordDialogContent()}
        positiveActionLoading={submitting}
        negativeActionDisabled={submitting}
        positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
        negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
        confirmFn={!submitting ? updateChangePasswordResultCallback : () => {}}
      />
    </Stack>
  );
};

export default React.memo(MerchantEmployee);
